import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { DollarSign, Search } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const BudgetModal = ({ isOpen, onClose, instruments, onUpdateBudget }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInstrument, setSelectedInstrument] = useState('');
  const [budgetData, setBudgetData] = useState({
    orcamentoNumero: '',
    custoCalibracao: '',
    custoManutencao: '',
  });
  const { toast } = useToast();

  const filteredInstruments = instruments ? instruments.filter((inst) =>
    inst && inst.nome && typeof inst.nome === 'string' && inst.nome.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  useEffect(() => {
    if (selectedInstrument) {
      const instrument = instruments.find((inst) => inst.id === selectedInstrument);
      if (instrument) {
        setBudgetData({
          orcamentoNumero: instrument.orcamentoNumero || '',
          custoCalibracao: instrument.custoCalibracao || '',
          custoManutencao: instrument.custoManutencao || '',
        });
      }
    }
  }, [selectedInstrument, instruments]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedInstrument) {
      toast({
        title: 'Selecione um instrumento',
        description: 'Por favor, selecione um instrumento para atualizar o orçamento.',
        variant: 'destructive',
      });
      return;
    }

    onUpdateBudget(selectedInstrument, {
      orcamentoNumero: budgetData.orcamentoNumero,
      custoCalibracao: parseFloat(budgetData.custoCalibracao) || 0,
      custoManutencao: parseFloat(budgetData.custoManutencao) || 0,
    });

    toast({
      title: 'Orçamento atualizado!',
      description: 'Os dados de orçamento foram salvos com sucesso.',
    });

    handleClose();
  };

  const handleClose = () => {
    setSearchTerm('');
    setSelectedInstrument('');
    setBudgetData({
      orcamentoNumero: '',
      custoCalibracao: '',
      custoManutencao: '',
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <DollarSign className="w-6 h-6 text-orange-600" />
            Gerenciar Orçamento
          </DialogTitle>
          <DialogDescription>
            Selecione um instrumento e atualize as informações de orçamento
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Instrument Selection */}
          <div className="space-y-2">
            <Label htmlFor="search">Buscar Instrumento</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Digite o nome do instrumento..."
                className="pl-10"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="instrument">Selecione o Instrumento</Label>
            <Select
              value={selectedInstrument}
              onValueChange={setSelectedInstrument}
            >
              <SelectTrigger>
                <SelectValue placeholder="Escolha um instrumento" />
              </SelectTrigger>
              <SelectContent>
                {filteredInstruments.length === 0 ? (
                  <div className="p-2 text-sm text-slate-500 text-center">
                    Nenhum instrumento encontrado
                  </div>
                ) : (
                  filteredInstruments.map((inst) => (
                    <SelectItem key={inst.id} value={inst.id}>
                      {inst.nome} - {inst.marca}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Budget Information */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4 space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-5 h-5 text-orange-600" />
              <h4 className="font-semibold text-orange-900">Informações de Orçamento</h4>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="orcamentoNumero">Orçamento Nº</Label>
                <Input
                  id="orcamentoNumero"
                  value={budgetData.orcamentoNumero}
                  onChange={(e) => setBudgetData({ ...budgetData, orcamentoNumero: e.target.value })}
                  placeholder="Ex: ORC-2024-001"
                />
              </div>
              <div>
                <Label htmlFor="custoCalibracao">Custo de Calibração (R$)</Label>
                <Input
                  id="custoCalibracao"
                  type="number"
                  step="0.01"
                  value={budgetData.custoCalibracao}
                  onChange={(e) => setBudgetData({ ...budgetData, custoCalibracao: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label htmlFor="custoManutencao">Custo de Manutenção (R$)</Label>
                <Input
                  id="custoManutencao"
                  type="number"
                  step="0.01"
                  value={budgetData.custoManutencao}
                  onChange={(e) => setBudgetData({ ...budgetData, custoManutencao: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
              Cancelar
            </Button>
            <Button 
              type="submit" 
              className="flex-1 bg-orange-600 hover:bg-orange-700"
              disabled={!selectedInstrument}
            >
              Salvar Orçamento
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BudgetModal;